import { IconButton } from '@common/ui/buttons/IconButton';
import { CachedImage } from '@common/ui/CachedImage';
import { DynamicImage } from '@common/ui/DynamicImage';

import { SuperlikeBadge } from '@entities/superlike/ui';

import { type IncomingSuperlikeCard } from '@services/SuperlikesService';

import styles from './SuperlikeLikeCard.module.scss';

export type SuperlikeLikeCardProps = {
  profile: IncomingSuperlikeCard;
  isPending: boolean;
  onOpen: () => void;
  onLike: () => void;
  onPass: () => void;
};

/**
 * A received superlike in the Likes tab (board 2.3): the photo unblurred
 * for everyone, the badge, the name, the message (two lines) or «Без
 * сообщения», and the pass and like buttons of the likes grid.
 */
export const SuperlikeLikeCard = ({
  profile,
  isPending,
  onOpen,
  onLike,
  onPass,
}: SuperlikeLikeCardProps) => (
  <article
    className={styles.superlikeLikeCard}
    data-test-id="superlike-like-card"
    data-pending={isPending}
    onClick={() => {
      if (!isPending) onOpen();
    }}
  >
    {profile.photos[0] && (
      // A user photo, like LikeCard's: a plain <img>. CachedImage fetch()es
      // its src, which the photo bucket's CORS refuses (it then shows
      // not-found.png); it is for the app's own icons.
      <DynamicImage
        testId="superlike-like-card-photo"
        src={profile.photos[0]}
        alt={`${profile.username}, ${profile.age}`}
        className={styles.photo}
      />
    )}
    <div className={styles.gradient} />

    <SuperlikeBadge
      testId="superlike-like-card-badge"
      text="Суперлайк"
      size="s"
      className={styles.badge}
    />

    <div className={styles.content}>
      <span className={styles.name} data-test-id="superlike-like-card-name">
        {profile.username}, {profile.age}
      </span>

      {profile.superlike.message ? (
        <span
          className={`${styles.message} ym-hide-content`}
          data-test-id="superlike-like-card-message"
        >
          «{profile.superlike.message}»
        </span>
      ) : (
        <span
          className={styles.noMessage}
          data-test-id="superlike-like-card-message"
        >
          Без сообщения
        </span>
      )}

      <div className={styles.buttons}>
        <IconButton
          testId="superlike-like-card-pass-button"
          variant="negativeSecondary"
          aria-label="Пропустить"
          disabled={isPending}
          onClick={(event) => {
            event.stopPropagation();
            onPass();
          }}
        >
          <CachedImage
            testId="cached-image"
            src="/icons/3d-icons/dislike.png"
            alt=""
          />
        </IconButton>
        <IconButton
          testId="superlike-like-card-like-button"
          variant="actionSecondary"
          className={styles.likeButton}
          aria-label="Лайк — будет мэтч"
          disabled={isPending}
          onClick={(event) => {
            event.stopPropagation();
            onLike();
          }}
        >
          <CachedImage
            testId="cached-image"
            src="/icons/3d-icons/like.png"
            alt=""
          />
        </IconButton>
      </div>
    </div>
  </article>
);
